import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAbout } from '../features/about/aboutSlice';
import { Helmet } from 'react-helmet-async';
import { getImageUrl } from '../utils/imageUrl';

const TentangKami = () => {
  const dispatch = useDispatch();
  const { about, isLoading } = useSelector((state) => state.about);
  const [activeSection, setActiveSection] = useState('profile');

  useEffect(() => {
    dispatch(getAbout());
  }, [dispatch]);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['profile', 'vision', 'mission'];
      const scrollPosition = window.scrollY + 200;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element && element.offsetTop <= scrollPosition && element.offsetTop + element.offsetHeight > scrollPosition) {
          setActiveSection(section);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 100,
        behavior: 'smooth'
      });
    }
  };

  if (isLoading) {
    return (
      <div className="py-12">
        <section className="container mx-auto px-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-8">
            <div className="animate-pulse">
              <div className="h-10 bg-gray-200 rounded-xl w-1/2 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-8"></div>
              <div className="h-8 bg-gray-200 rounded-xl w-1/3 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  const sections = [
    { id: 'profile', label: 'Profil Penerbit', show: about?.profile },
    { id: 'vision', label: 'Visi', show: about?.vision },
    { id: 'mission', label: 'Misi', show: about?.mission }
  ].filter(section => section.show);

  return (
    <>
      <Helmet>
        <title>Tentang Kami | Penerbit Literasiologi</title>
      </Helmet>

      <div className="py-12">
        <section className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Tentang Kami</h1>
            {about?.slogan && (
              <p className="text-xl text-gray-600 italic">{about.slogan}</p>
            )}
          </div>

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sticky Menu */}
            {sections.length > 0 && (
              <div className="hidden lg:block">
                <div className="sticky top-24">
                  <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Navigasi</h3>
                    <nav className="space-y-2">
                      {sections.map((section) => (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
                            activeSection === section.id
                              ? 'bg-dark-blue-50 text-dark-blue-700 font-medium'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {section.label}
                        </button>
                      ))}
                    </nav>
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="lg:col-span-3 space-y-8">
              {/* Profile */}
              {about?.profile && (
                <div id="profile" className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">Profil Penerbit</h2>
                  <div className="text-gray-700 leading-relaxed prose-content mb-8" dangerouslySetInnerHTML={{ __html: about.profile }} />
                  
                  {/* IKAPI Certificate */}
                  {about?.ikapiCertificate && (
                    <div className="pt-8 border-t border-gray-200">
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">Sertifikat IKAPI</h3>
                      <img
                        src={getImageUrl(about.ikapiCertificate)}
                        alt="Sertifikat IKAPI"
                        className="max-w-md rounded-xl border border-gray-200 shadow-sm"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Vision */}
              {about?.vision && (
                <div id="vision" className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">Visi</h2>
                  <div className="text-gray-700 leading-relaxed prose-content" dangerouslySetInnerHTML={{ __html: about.vision }} />
                </div>
              )}

              {/* Mission */}
              {about?.mission && (
                <div id="mission" className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6">Misi</h2>
                  <div className="text-gray-700 leading-relaxed prose-content" dangerouslySetInnerHTML={{ __html: about.mission }} />
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          {sections.length > 0 && (
            <div className="lg:hidden mt-8">
              <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm">
                <div className="flex flex-wrap gap-2 justify-center">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`px-4 py-2 rounded-xl text-sm transition-all ${
                        activeSection === section.id
                          ? 'bg-dark-blue-700 text-white font-medium'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {section.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </>
  );
};

export default TentangKami;
